import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Shield, LockKeyhole, Fingerprint } from "lucide-react";
import { signInWithPasskey, checkHasPasskey, isWebAuthnSupported } from "@/lib/passkey";

export default function LoginPage() {
  const { login, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [pkLoading, setPkLoading] = useState(false);

  // Auto-detect whether this email has a passkey (debounced)
  useEffect(() => {
    if (!email || !isWebAuthnSupported()) { setHasPasskey(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const yes = await checkHasPasskey(email);
      if (!cancelled) setHasPasskey(yes);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [email]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await login(email, password);
      if (res.mfa_required) {
        navigate("/two-factor", { state: { challenge_token: res.challenge_token, mfa_method: res.mfa_method } });
      } else if (res.user?.must_change_password) {
        navigate("/change-password");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg grid lg:grid-cols-5">
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden border-r border-border">
        <img
          src="https://images.unsplash.com/photo-1554478416-6061b7e29d52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NzUwMzc4MHww&ixlib=rb-4.1.0&q=85"
          alt="office"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-white/30 to-white/0" />
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground font-display font-bold">HR</div>
            <div>
              <div className="font-display font-bold">HR Track</div>
              <div className="tiny-label">Workforce suite</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="tiny-label mb-3">Built for HR & Operations</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-foreground">
              Hours, holidays, and compliance — without the spreadsheet.
            </h1>
            <p className="mt-4 text-base text-muted-foreground">
              Secure, auditable, and tuned to Irish working-time rules. Sign in to your company workspace.
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> End-to-end audit</div>
            <div className="flex items-center gap-1.5"><LockKeyhole className="w-3.5 h-3.5" /> 2FA enforced</div>
          </div>
        </div>
      </div>

      <div className="flex lg:col-span-2 items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md border-border shadow-none">
          <CardContent className="p-8">
            <div className="tiny-label">Sign in</div>
            <h2 className="font-display text-2xl font-bold mt-2">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Use your work email and password.</p>

            <form className="mt-8 space-y-5" onSubmit={onSubmit}>
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  data-test="login-email"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  data-test="login-password"
                  className="mt-1.5 h-11"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11" data-testid="login-submit-button" data-test="login-submit">
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            {hasPasskey && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="h-px bg-border flex-1" /> or <div className="h-px bg-border flex-1" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11"
                  onClick={async () => {
                    setPkLoading(true);
                    try {
                      const res = await signInWithPasskey(email);
                      await refreshMe();
                      if (res.user?.must_change_password) navigate("/change-password");
                      else navigate("/dashboard");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || err?.message || "Passkey sign-in failed");
                    } finally { setPkLoading(false); }
                  }}
                  disabled={pkLoading || !email}
                  data-testid="login-passkey-button"
                >
                  <Fingerprint className="w-4 h-4 mr-2" />
                  {pkLoading ? "Authenticating…" : "Sign in with passkey"}
                </Button>
              </div>
            )}
            <p className="mt-6 text-xs text-muted-foreground">
              <a href="/forgot-password" className="font-medium underline hover:text-foreground" data-testid="forgot-link">
                Forgot your password?
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
